// deps
import React, { ReactElement, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// dls
import { isEmpty } from "@swiggy-private/js-utils";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import { Toast } from "@swiggy-private/dls-web-recipe-food-web/dist/components/Toast";

// types
import { QrCodeActions, ResponseContext, ToastType } from "@swiggy-private/portal-dineout-context";
import { ToastContainer, IconContainer, TOAST_CONTAINER_CLASSNAME } from "./styles";
import { IToastProps } from "./types";
import { BOTTOM_ELEMENT_IDS } from "../../constants/toast";

function GenericToast(props: IToastProps): ReactElement | null {
    const { state, dispatch } = useContext(ResponseContext);
    const [hasBottomElement, setHasBottomElement] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Check if any bottom element exists in DOM.
    useEffect(() => {
        if (typeof window === "undefined" || typeof document === "undefined") return; // to avoid ssr error

        const checkBottomElements = () => {
            const hasElement = BOTTOM_ELEMENT_IDS.some((id) => document.getElementById(id) !== null);
            setHasBottomElement(hasElement);
        };

        checkBottomElements();

        // Watch for DOM changes
        if (typeof MutationObserver !== "undefined") {
            const observer = new MutationObserver(checkBottomElements);
            observer.observe(document.body, { childList: true, subtree: true });
            return () => observer.disconnect();
        }
    }, []);

    const onDismiss = useCallback(() => {
        dispatch({
            type: QrCodeActions.DISMISS_TOAST,
            payload: {},
        });
    }, [dispatch]);

    const Icon = useMemo(() => {
        switch (state?.toast?.type) {
            case ToastType.ERROR:
                return (
                    <IconContainer>
                        <SpriteIconWeb iconName="errorFilled24" strokeColor="negative" fillColor="negative" />
                    </IconContainer>
                );
            case ToastType.INFO:
                return (
                    <IconContainer>
                        <SpriteIconWeb iconName="info24" />
                    </IconContainer>
                );
            case ToastType.SUCCESS_TICK:
                return (
                    <IconContainer>
                        <SpriteIconWeb iconName="tick24" strokeColor="text_inverse_Highest_Emphasis" />
                    </IconContainer>
                );
            case ToastType.SUCCESS:
                return (
                    <IconContainer>
                        <SpriteIconWeb iconName="tickFilled24" strokeColor="positive" fillColor="positive" />
                    </IconContainer>
                );
            default:
                return null;
        }
    }, [state?.toast?.type]);

    useEffect(() => {
        if (!isEmpty(state?.toast?.autoDismissTime) && !state?.toast?.autoDismiss) {
            timerRef.current = setTimeout(onDismiss, state.toast.autoDismissTime);
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [state?.toast?.autoDismiss, state?.toast?.autoDismissTime, onDismiss]);

    return (
        <>
            {state.toast?.show && (
                <ToastContainer hasBottomBar={hasBottomElement}>
                    <Toast
                        message={state.toast.message ?? props.message}
                        leftAsset={Icon}
                        containerClass={TOAST_CONTAINER_CLASSNAME}
                        onDismiss={onDismiss}
                        autoDismiss={state.toast.autoDismiss}
                        show
                        removeHardcodedColorMode={true}
                        textColor="text_inverse_Highest_Emphasis"
                    />
                </ToastContainer>
            )}
        </>
    );
}

export default memo(GenericToast);
